// @ts-nocheck
/**
 * Add SMS Webhook — redesigned to obd3-gui pattern
 * (ScrollPage + PageHeader + Card sections).
 * Backend quirk preserved from v1: request_type "POST" is sent as "WHATSAPP".
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { postApiCall } from '@/service/httpCall';
import { API_SMSWEBHOOK_CREATE } from '@/service/url';
import { ROUTES, FLASH_MESSAGE_ACTION_OBJECT, MODULE_ACL } from '@/utilities/constant';
import ApiBuilder from './components/ApiBuilder';
import CopyButton from './components/CopyButton';
import InstructionCard from './components/InstructionCard';
import { fetchAndSetFlashMessage } from '@/service/fetchFlashMessage';

const CreateSMSWebhook = () => {
  const navigate = useNavigate();

  const [webhook_name, setWebhookName] = useState('');
  const [request_type, setRequestType] = useState('');
  const [url, setUrl] = useState('');
  const [sms_text, setSmsText] = useState('');
  const [payload, setPayload] = useState('');
  const [header, setHeader] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState<any>(null);

  useEffect(() => {
    fetchAndSetFlashMessage(
      (msg: any) =>
        msg.module_name === MODULE_ACL.SMS_WEBHOOK &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.ADD &&
        msg.type === 'Flash Message',
      setAlertMessage,
    );
  }, []);

  const apiMethod = useMemo(() => request_type || 'GET', [request_type]);

  const formattedHeader = useMemo(() => {
    const h = (header || '').trim();
    if (!h) return '';
    try {
      return JSON.stringify(JSON.parse(h), null, 2);
    } catch {
      return h;
    }
  }, [header]);

  const handleApiBuilderChange = (apiState: any) => {
    setUrl(apiState?.url || '');
    if (!apiState) {
      setHeader('');
      return;
    }
    if (apiState.headerMode === 'raw') {
      setHeader((apiState.rawHeaders || '').trim());
      return;
    }
    const headerObj = (apiState.headers || []).reduce((acc: any, h: any) => {
      const key = (h?.key || '').trim();
      if (!key) return acc;
      acc[key] = h?.value ?? '';
      return acc;
    }, {});
    setHeader(Object.keys(headerObj).length ? JSON.stringify(headerObj) : '');
  };

  const handleUseUrl = (u: string) => setUrl(u || '');
  const handleBuilderResponse = (bodyString: string) => setPayload(bodyString || '');

  const validate = () => {
    const name = webhook_name.trim();
    const u = url.trim();
    const type = request_type;
    const urlRegex = /^(https?:\/\/)([\w\-._~:/?#[\]@!$&'()*+,;=]+)$/i;

    if (!name || !u || !type) {
      toast.error('Please fill all required fields.');
      return false;
    }
    if (!urlRegex.test(u)) {
      toast.error('Please enter a valid Webhook URL (must start with http:// or https://).');
      return false;
    }
    if (header && header.trim()) {
      try {
        JSON.parse(header);
      } catch {
        toast.error('Headers must be valid JSON (use Api Builder headers section).');
        return false;
      }
    }
    if (type === 'GET' && !sms_text.trim()) {
      toast.error('SMS Text is required for GET requests.');
      return false;
    }
    if (type === 'POST' && !payload.trim()) {
      toast.error('Payload is required for POST requests.');
      return false;
    }
    return true;
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!validate()) return;
    setShowConfirmModal(true);
  };

  const submitWebhook = async () => {
    setShowConfirmModal(false);
    setSaving(true);
    const apiRequestType = request_type === 'POST' ? 'WHATSAPP' : 'GET';
    const body = {
      webhook_name: webhook_name.trim(),
      url: url.trim(),
      request_type: apiRequestType,
      sms_text: request_type === 'GET' ? sms_text : null,
      payload: request_type === 'POST' ? payload : null,
      header: header && header.trim() ? header.trim() : null,
      status: '1',
    };
    try {
      await postApiCall(API_SMSWEBHOOK_CREATE, body);
      toast.success('SMS Webhook created successfully!');
      navigate(`/${ROUTES.SMSWEBHOOK.LIST}`);
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ScrollPage>
      {alertMessage && (
        <div
          className="sms-webhook-marquee mb-4 overflow-hidden rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-700 dark:text-emerald-400"
          role="status"
        >
          <div
            className="sms-webhook-marquee__track"
            style={{ animationDuration: `${String(alertMessage).length * 0.2 + 15}s` }}
          >
            <span className="sms-webhook-marquee__item">{alertMessage}</span>
            <span className="sms-webhook-marquee__item" aria-hidden="true">
              {alertMessage}
            </span>
          </div>
          <style>{`
            .sms-webhook-marquee__track {
              display: flex;
              width: max-content;
              white-space: nowrap;
              animation-name: smsWebhookMarquee;
              animation-timing-function: linear;
              animation-iteration-count: infinite;
            }
            .sms-webhook-marquee:hover .sms-webhook-marquee__track {
              animation-play-state: paused;
            }
            .sms-webhook-marquee__item {
              padding-right: 3rem;
            }
            @keyframes smsWebhookMarquee {
              from { transform: translateX(0); }
              to { transform: translateX(-50%); }
            }
          `}</style>
        </div>
      )}

      <PageHeader
        title="Add SMS Webhook"
        description="Configure an outbound SMS/WhatsApp callback. Use the API builder to test it before saving."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.SMSWEBHOOK.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back to webhooks
            </Link>
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px] lg:items-start">
      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        {/* Basics */}
        <Card>
          <CardHeader>
            <CardTitle>Basics</CardTitle>
            <CardDescription>Name your webhook and pick the HTTP method.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="webhook_name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="webhook_name"
                required
                type="text"
                placeholder="e.g. SmsDeliveryCallback"
                value={webhook_name}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[A-Za-z]*$/.test(value)) setWebhookName(value);
                }}
                autoComplete="off"
                title="Only letters allowed."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="request_type">
                Method <span className="text-destructive">*</span>
              </Label>
              <Select value={request_type} onValueChange={setRequestType}>
                <SelectTrigger id="request_type">
                  <SelectValue placeholder="Select request type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Request builder */}
        {request_type && (
          <Card>
            <CardHeader>
              <CardTitle>Request builder</CardTitle>
              <CardDescription>
                Paste a cURL or configure URL and headers. Click <b>Test</b> to preview the
                response.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ApiBuilder
                initialUrl={url}
                initialMethod={apiMethod}
                onUseUrl={handleUseUrl}
                onMethod={(m: any) => {
                  const next = String(m || '').toUpperCase() === 'GET' ? 'GET' : 'POST';
                  setRequestType(next);
                }}
                onResponse={handleBuilderResponse}
                onStateChange={handleApiBuilderChange}
              />
            </CardContent>
          </Card>
        )}

        {/* Body */}
        {request_type === 'GET' && (
          <Card>
            <CardHeader>
              <CardTitle>SMS Text</CardTitle>
              <CardDescription>
                Paste the SMS message body that will be sent. Required for GET webhooks.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="sms_text">
                  Message <span className="text-destructive">*</span>
                </Label>
                <CopyButton text={sms_text} />
              </div>
              <Textarea
                id="sms_text"
                required
                name="sms_text"
                value={sms_text}
                rows={6}
                className="!bg-muted/40 !shadow-none !border-0 font-mono text-xs text-foreground"
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\s+$/.test(value)) return;
                  setSmsText(value);
                }}
                onBlur={(e) => setSmsText(e.target.value.trim())}
              />
            </CardContent>
          </Card>
        )}

        {request_type === 'POST' && (
          <Card>
            <CardHeader>
              <CardTitle>Payload</CardTitle>
              <CardDescription>
                Body sent when the webhook fires. Replace the mobile number with{' '}
                <code className="rounded bg-muted px-1 text-xs">{'{PHONE}'}</code>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="payload">
                  JSON payload <span className="text-destructive">*</span>
                </Label>
                <CopyButton text={payload} />
              </div>
              <Textarea
                id="payload"
                required
                name="payload"
                value={payload}
                rows={6}
                className="!bg-muted/40 !shadow-none !border-0 font-mono text-xs text-foreground"
                onChange={(e) => setPayload(e.target.value)}
              />
            </CardContent>
          </Card>
        )}

        {header && header.trim() && (
          <Card>
            <CardHeader>
              <CardTitle>Headers</CardTitle>
              <CardDescription>
                Read-only view of the headers configured in the request builder.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-end">
                <CopyButton text={formattedHeader} />
              </div>
              <Textarea
                name="headers"
                value={formattedHeader}
                disabled
                rows={5}
                className="!bg-muted/40 !shadow-none !border-0 font-mono text-xs text-foreground"
              />
            </CardContent>
          </Card>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="ghost">
            <Link to={`/${ROUTES.SMSWEBHOOK.LIST}`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Adding…' : 'Add SMS webhook'}
          </Button>
        </div>
      </form>

      <InstructionCard />
      </div>

      <ConfirmDialog
        open={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        title="Confirm submission"
        description={
          <>
            Is the{' '}
            <span className="font-medium text-foreground">
              {request_type === 'POST' ? 'payload' : 'SMS text'}
            </span>{' '}
            correct? The webhook will be saved and active immediately.
          </>
        }
        confirmLabel="Yes, add webhook"
        onConfirm={submitWebhook}
      />
    </ScrollPage>
  );
};

export default CreateSMSWebhook;
